import { useMemo, useRef, useState } from 'react';
import { useApp } from '../store';
import { useEscapeToClose, useFocusTrap } from '../motion';
import { displayName } from '../people';
import { blockerOf, stillBlocked } from '../blocked';
import { MONTHS } from '../when';
import { STATUS_LABELS, type IssueStatus } from '../types';
import CardFeed from './CardFeed';
import VendorTag from './VendorTag';
import Icon from './Icon';

/*
 * THE MY WORK CARD PANEL — the workstation (Vinnie, 6 Oct: "I am still taken to
 * the board when clicking on a card on a different board").
 *
 * Clicking a card in My Work — an agenda row, a drilled-in blocker, or an inbox
 * call-out — no longer switches the whole app to that card's board. It opens the
 * card HERE, right-docked over My Work, and you reply, react, post an update and
 * move it, all without leaving. The store fetched the full card (its comments
 * nested) by board id with no setProject, so none of the board-switch load.
 *
 * What stays on the full card. The board-coupled edits — area, phase, sprint, the
 * blocker typeahead, the title, delete, answering the reporter — are not inline;
 * they need the board's own areas/phases/people loaded. The footer's "Open full
 * card" is the one bridge to them, and it plants the "← Back to My work" trail so
 * the round trip returns here. Everything a person does day-to-day on their own
 * cards — read the feed, reply, react, say where it stands, move it — is inline.
 *
 * Built on the app's own slide-over (InvoiceDrawer's .inv-drawer / .drw-* frame),
 * so it is the drawer the budget ledger already uses — no new visual language.
 */

/** 'YYYY-MM-DD' → '4 Oct', read off the parts so no timezone shifts the day. */
function fmtDay(iso?: string | null): string {
  if (!iso) return '';
  const [, m, d] = iso.split('-').map(Number);
  if (!m || !d) return '';
  return `${d} ${MONTHS[m - 1]}`;
}

export default function MyWorkCard({
  boardId,
  boardName,
  leaving,
  onClose,
  onOpenFull,
}: {
  boardId: string;
  boardName: string;
  leaving: boolean;
  onClose: () => void;
  /** The escape hatch: take the person to the full card on its own board. */
  onOpenFull: () => void;
}) {
  const card = useApp((s) => s.inlineCard);
  const loading = useApp((s) => s.inlineCardLoading);
  const error = useApp((s) => s.inlineCardError);
  const role = useApp((s) => s.shownRole);
  const profiles = useApp((s) => s.profiles);
  const areasByBoard = useApp((s) => s.portfolioAreas);
  const postUpdateInline = useApp((s) => s.postUpdateInline);
  const addCommentInline = useApp((s) => s.addCommentInline);
  // The In Staging option follows THIS card's own board, not the active one —
  // read from the portfolio project list by the card's board id.
  const boardStagingOn = useApp((s) => s.projects.find((p) => p.id === boardId)?.stagingEnabled === true);
  // This card's own board's cards (the cross-board cache My Work already holds),
  // so the blocked tag can ask stillBlocked — a blocker whose card is finished is
  // no longer a block, and must not keep showing "Blocked" here (L157). Select the
  // whole (stable) map and read it outside the selector; `?? []` INSIDE a selector
  // would mint a new array each render and loop (the "no selector builds a value"
  // rule), which is why MyWork.tsx selects the map the same way.
  const portfolioIssues = useApp((s) => s.portfolioIssues);
  const boardCards = portfolioIssues.get(boardId) ?? [];

  const panelRef = useRef<HTMLElement>(null);
  const [draft, setDraft] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);
  // One close path for BOTH the × and Escape — an unposted draft raises the "close
  // anyway?" guard whichever way you reach for it. The × used to call onClose
  // directly and drop the draft with no warning while Escape asked (the L72 class
  // of bug, re-made on this panel); routing both through requestClose closes it.
  const requestClose = () => (draft && !confirmLeave ? setConfirmLeave(true) : onClose());
  useEscapeToClose(requestClose, !leaving);
  useFocusTrap(panelRef, !leaving);

  // The area NAME is resolved from the card's OWN board (portfolioAreas is keyed
  // by board), not the active board — so an off-board card still names its area.
  const areaName = useMemo(() => {
    if (!card?.area) return '';
    const areas = areasByBoard.get(boardId) ?? [];
    return areas.find((a) => a.id === card.area)?.name ?? '';
  }, [card?.area, areasByBoard, boardId]);

  const ref = card?.ref ?? '';
  const title = card?.title ?? '';
  const status = (card?.status ?? 'todo') as IssueStatus;
  const settled = status === 'done' || status === 'wontdo';
  const blocker = card && !settled && stillBlocked(card, boardCards) ? blockerOf(card) : '';

  // A quick "Mark done" is a pure status move — the same entry the board would
  // write, posted by board id. It rides the shared builder via postUpdateInline.
  const markDone = async () => {
    if (!card || busy) return;
    setBusy(true);
    try {
      await postUpdateInline({ text: '', status: 'done' });
    } catch {
      /* the store surfaces the problem on the banner; leave the panel open */
    } finally {
      setBusy(false);
    }
  };

  const facts: { k: string; v: React.ReactNode }[] = [];
  if (card) {
    facts.push({
      k: 'Accountable',
      v: card.assignee ? (
        <>
          {displayName(card.assignee, profiles)}
          <VendorTag who={card.assignee} profiles={profiles} compact />
        </>
      ) : (
        <span className="mwc-fact-none">Unassigned</span>
      ),
    });
    if (card.dueDate) facts.push({ k: 'Due', v: fmtDay(card.dueDate) });
    if (card.epic) facts.push({ k: 'Epic', v: card.epic });
    if (areaName) facts.push({ k: 'Area', v: areaName });
  }

  return (
    <aside
      ref={panelRef}
      tabIndex={-1}
      className={`inv-drawer mw-card ${leaving ? 'is-leaving' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-label={title ? `${ref} ${title}` : 'Card'}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="drw-head">
        <div className="lcd-headl">
          <span className="lcd-context">
            {ref}
            {ref && ' · '}
            {boardName}
          </span>
          <span className="id-title">{title || (loading ? 'Opening…' : 'Card')}</span>
        </div>
        <div className="id-head-right">
          {card && (
            <span className="mwc-status" style={{ ['--tone' as string]: `var(--s-${status})` }}>
              <span className="mwc-status-dot" />
              {STATUS_LABELS[status]}
            </span>
          )}
          <button className="drw-close" onClick={requestClose} title="Close (Escape)" aria-label="Close (Escape)">
            <Icon name="close" size={17} />
          </button>
        </div>
      </div>

      <div className="drw-body id-body">
        {loading && !card ? (
          <div className="mwc-state">Opening the card…</div>
        ) : error && !card ? (
          <div className="mwc-state mwc-state-err">{error}</div>
        ) : card ? (
          <>
            {facts.length > 0 && (
              <dl className="mwc-facts">
                {facts.map((f) => (
                  <div className="mwc-fact" key={f.k}>
                    <dt>{f.k}</dt>
                    <dd>{f.v}</dd>
                  </div>
                ))}
              </dl>
            )}

            {blocker && (
              <div className="mwc-blk">
                <span className="mwc-blk-tag">Blocked</span>
                <span className="mwc-blk-r">{blocker}</span>
              </div>
            )}

            {/* The facts above draw instantly from the light card My Work already
                held; the conversation needs the full card's nested comments, so it
                alone waits on the fetch — a slim line here rather than a blank, and
                never a false "no updates yet" before the real feed arrives. */}
            {loading ? (
              <div className="mwc-state mwc-feed-wait">Loading the conversation…</div>
            ) : (
              <CardFeed
                issue={card}
                canPost
                role={role}
                onDraft={setDraft}
                inline={{ post: postUpdateInline, reply: addCommentInline }}
                stagingEnabled={boardStagingOn}
              />
            )}
          </>
        ) : null}
      </div>

      <div className="drw-foot">
        {confirmLeave ? (
          <>
            <span className="id-foot-note">You have something unsent — close anyway?</span>
            <span className="id-foot-btns">
              <button className="btn btn-small" onClick={() => setConfirmLeave(false)}>
                Keep editing
              </button>
              <button className="btn btn-primary btn-small" onClick={onClose}>
                Close
              </button>
            </span>
          </>
        ) : (
          <>
            <span className="id-foot-note">
              {settled ? (
                <>This card is {STATUS_LABELS[status].toLowerCase()}.</>
              ) : (
                <>Reply, react and say where it stands here — the rest lives on the card.</>
              )}
            </span>
            <span className="id-foot-btns">
              {card && !settled && (
                <button className="btn btn-small" disabled={busy} onClick={() => void markDone()}>
                  <Icon name="check" size={14} /> {busy ? 'Marking…' : 'Mark done'}
                </button>
              )}
              <button className="btn btn-small" onClick={onOpenFull} title={`Open the full card on ${boardName}`}>
                Open full card <Icon name="chevron" size={14} />
              </button>
            </span>
          </>
        )}
      </div>
    </aside>
  );
}
